import { Card, Icon, Text, TextButton } from '@joor/design-system'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { ErrorBoundary } from 'react-error-boundary'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'

import { OrderStatus as AtlasOrderStatus } from '__generated__/atlas-types'

import { getOrderEditProductsLink } from 'utils/links'
import { fromGlobalId } from 'utils/transformations/graphql'

import {
  isEditApprovedShippedRestricted,
  isLiteRetailer as isLiteRetailerSelector,
} from 'store/currentUser/selectors'

import { OrderData } from '../../Overview'
import {
  Error,
  Loading,
  ProductsEmpty,
  ProductsList,
  SummaryAmounts,
} from './components'
import messages from './constants/messages'
import { FetchMoreFn, Product, ProductAmounts } from './types'
import { areOrderProductsEditable } from './utils'
import AddProductsLink from 'features/Orders/Review/components/AddProductsButton/AddProductsLink'
import { useOrderButtonState } from 'features/Orders/Review/components/AddProductsButton/addProductsButton.hooks'
import { ReviewPageIds } from 'features/Orders/Review/review.ids'

const StyledHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  & div {
    font-size: var(--size-font-base);

    &:hover {
      text-decoration: underline;
    }
  }
`

export function ProductsWidget(props: ProductsWidgetProps) {
  const { or2023ProductsEditModal } = useFlags()
  const {
    setIsProductsModalOpen,
    products,
    productAmounts,
    id,
    webOrderLookupId,
    status,
    loading,
    fetchAllProducts,
    priceType,
    collections,
    brand,
  } = props
  const isLiteRetailer = useSelector(isLiteRetailerSelector)
  const isEditApprovedShippedOrdersRestricted = useSelector(
    isEditApprovedShippedRestricted,
  )

  const canEditProducts = areOrderProductsEditable(
    status as AtlasOrderStatus | undefined,
    isLiteRetailer,
    isEditApprovedShippedOrdersRestricted,
  )

  const handleClickActionButton = () => {
    const webOrderId = fromGlobalId(webOrderLookupId).id
    const orderId = fromGlobalId(id).id
    const productsPath = getOrderEditProductsLink({
      order: {
        id: orderId || '',
        webOrderLookupId: webOrderId || '',
        status: status,
      },
      canEditProducts,
    })
    const link = `${window.location.origin}${productsPath}`
    window.open(link, '_blank')
  }

  const onClickActionButton = or2023ProductsEditModal
    ? setIsProductsModalOpen
    : handleClickActionButton

  return (
    <ErrorBoundary
      fallback={
        <Error
          actionButtonLabel="Edit"
          onClickActionButton={onClickActionButton}
        />
      }
    >
      <ProductsWidgetContent
        {...props}
        isEditable={canEditProducts}
        or2023ProductsEditModal={or2023ProductsEditModal}
        onClickActionButton={onClickActionButton}
        orderDetails={{
          status,
          collections,
          brand,
          priceType,
        }}
        productsData={{
          products,
          productAmounts,
          status,
          fetchAllProducts,
          loading,
        }}
      />
    </ErrorBoundary>
  )
}

function ProductsWidgetContent({
  className = '',
  testId,
  onClickActionButton,
  orderDetails,
  isEditable,
  productsData,
}: ProductsWidgetContentProps) {
  const { products, productAmounts, fetchAllProducts, loading } = productsData

  const errors = useOrderButtonState({
    order: {
      collections: orderDetails?.collections,
      status: orderDetails?.status,
      products: productsData?.products,
    },
  })

  const shouldHideButton = errors.some((error) => error.isButtonHidden)
  const shouldDisableButton = errors.some((error) => error.isButtonDisabled)
  const reasons = errors.map((error) => error.reason)

  const HeaderAction = () => (
    <StyledHeaderWrapper>
      {!shouldHideButton && !loading && (
        <AddProductsLink
          id={ReviewPageIds.AddProductsId}
          order={orderDetails}
          isLink={true}
          isDisabled={shouldDisableButton}
          reasons={reasons}
        />
      )}

      {isEditable && (
        <>
          <span>|</span>
          <TextButton
            data-testid={ReviewPageIds.ProductsEditButtonId}
            onClick={onClickActionButton}
            disabled={loading}
            id={ReviewPageIds.ProductsEditButtonId}
          >
            Edit
          </TextButton>
        </>
      )}
    </StyledHeaderWrapper>
  )

  return (
    <StyledCard
      testId={testId}
      className={className}
      title={messages.widgetHeader}
      headerAction={<HeaderAction />}
    >
      <Content
        loading={loading}
        products={products}
        productAmounts={productAmounts}
        fetchAllProducts={fetchAllProducts}
        orderDetails={orderDetails}
        reasons={reasons || ''}
        isButtonDisabled={shouldDisableButton}
        isButtonHidden={shouldHideButton}
      />
    </StyledCard>
  )
}

function Content({
  loading,
  products,
  productAmounts,
  fetchAllProducts,
  orderDetails,
  reasons,
  isButtonDisabled,
  isButtonHidden,
}: WidgetContentProps) {
  if (loading) {
    return <Loading />
  }

  if (!products.length) {
    return (
      <ProductsEmpty
        orderDetails={orderDetails}
        isButtonDisabled={isButtonDisabled}
        isButtonHidden={isButtonHidden}
        reasons={reasons}
      />
    )
  }

  return (
    <>
      {reasons &&
        reasons?.length > 0 &&
        reasons?.map((reason) => <ErrorMessage message={reason} />)}
      <SummaryAmounts productAmounts={productAmounts} />
      <Divider />
      <ProductsList
        products={products}
        onLoadAllProds={fetchAllProducts}
        totalCount={productAmounts.totalProducts}
      />
    </>
  )
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <ErrorMessageWrapper>
      <Icon iconName="exclamation" variant="DANGER" plain />
      <Text color="error-500" lineHeight="16px">
        {message}
      </Text>
    </ErrorMessageWrapper>
  )
}

type ProductsWidgetProps = {
  className?: string
  testId?: string
  setIsProductsModalOpen?: () => void
  id: string
  webOrderLookupId: string
  status: OrderData['status']
  products: Product[]
  productAmounts: ProductAmounts
  fetchAllProducts: FetchMoreFn
  collections?: OrderData['collections']['edges']
  brand?: NonNullable<OrderData['brand']>
  priceType: OrderData['priceType']
  loading: boolean
}

type ProductsWidgetContentProps = {
  className?: string
  testId?: string
  or2023ProductsEditModal: boolean
  onClickActionButton?: () => void
  orderDetails: {
    collections?: OrderData['collections']['edges']
    brand?: NonNullable<OrderData['brand']>
    priceType: OrderData['priceType']
    status: OrderData['status']
  }
  productsData?: any
  isEditable: boolean
}

type WidgetContentProps = {
  loading: boolean
  products: Product[]
  productAmounts: ProductAmounts
  fetchAllProducts: FetchMoreFn
  orderDetails: {
    collections?: OrderData['collections']['edges']
    brand?: NonNullable<OrderData['brand']>
    priceType: OrderData['priceType']
    status: OrderData['status']
  }
  reasons: string[]
  isButtonDisabled: boolean
  isButtonHidden: boolean
}

export const StyledCard = styled(Card)`
  & > section {
    margin: 0;
    padding: 0;
  }
`

const Divider = styled.div`
  border-bottom: 1px solid var(--color-primary-100);
`

const ErrorMessageWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px 0 10px;
`
